import {
	BackIcon,
	Box,
	Button,
	Flex,
	Heading,
	IconButton,
	Image,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalFooter,
	ModalHeader,
	Stack,
	Text,
	ThemeFontSizeVariable,
	ThemeSpaceVariable,
} from "@caspeco/casper-ui-library";
import myProfile from "../../assets/post-login/MyProfile.png";
import progressSpinner from "../../assets/post-login/ProgressSpinner.png";
import { type JSX, useEffect, useRef, useState } from "react";
import { useUserContext } from "../../hooks/use-user-context";
// eslint-disable-next-line caspeco-react/discourage-chakra-import
import { Progress } from "@chakra-ui/react"; // v3 compound component
import { PostLoginOnboardningName } from "./post-login-onboarding-name";
import { PostLoginOnboardningAdress } from "./post-login-onboarding-adress";
import { PostLoginOnboardningContactInfo } from "./post-login-onboarding-contact-info";
import { PostLoginOnboardningContacts } from "./post-login-onboarding-contacts";
import { PostLoginOnboardningBankAccount } from "./post-login-onboarding-back-account";
import { PostLoginOnboardningDone } from "./post-login-onboarding-done";
import PasswordlessModal from "../../components/passwordless-modal";
import { PushNotificationModal } from "../../components/pushnotification-modal";
import { useLocalStorage } from "usehooks-ts";
import { isTodayOrBefore } from "../../utils/date-helper";
import { isNullOrWhiteSpace } from "../../utils/string-helper";
import { isApp } from "../../utils/app-helper";
import { useTranslation } from "react-i18next";
import { useAppContext } from "../../hooks/use-app-context";
import { OnboadingSystem, SystemOnboadringModal } from "../../components/system-onboarding-modal";
import { PostLoginOnboardingColleaguesShifts } from "./post-login-onboadring-colleagues-shifts";
import { isToday } from "date-fns";

type props = {
	children: JSX.Element;
};

type ContinueButtonProps = {
	isDisabled: boolean;
	save: () => Promise<boolean>;
};

type PostLoginOnboarding = {
	showBiometric: boolean;
	showNotification: boolean;
	showAtTime?: Date;
	onboardedSystems: string[];
};

export type PostLoginOnboardingStepsProps = {
	setContinueButton: (setContinueButton: ContinueButtonProps) => void;
};

export const PostLoginOnboarding = ({ children }: props) => {
	const { t } = useTranslation();
	const { user, systems } = useUserContext();
	const { safeArea } = useAppContext();

	const [showContactsModal, setShowContactsModal] = useState(false);
	const openingContactsModal = useRef(false);
	const [stepperCount, setStepperCount] = useState(1);
	const [stepContent, setStepContent] = useState<JSX.Element[]>([]);
	const [continueButton, setContinueButton] = useState<ContinueButtonProps>();
	const [isLoading, setIsLoading] = useState(false);
	const [onboarding, setOnboarding] = useLocalStorage<PostLoginOnboarding>("post-login-onboarding", {
		showBiometric: isApp(),
		showNotification: isApp(),
		showAtTime: new Date(),
		onboardedSystems: [],
	});

	const haveMissingInformation = () => {
		return (
			isNullOrWhiteSpace(user?.firstname) ||
			isNullOrWhiteSpace(user?.lastname) ||
			isNullOrWhiteSpace(user?.address) ||
			isNullOrWhiteSpace(user?.city) ||
			isNullOrWhiteSpace(user?.country) ||
			isNullOrWhiteSpace(user?.email) ||
			isNullOrWhiteSpace(user?.mobilePhone) ||
			(systems.length === 1 && user?.systemBankAccounts.length === 0)
		);
	};

	const onClose = () => {
		let showAgainTime;

		if (haveMissingInformation()) {
			showAgainTime = new Date();
			showAgainTime.setDate(showAgainTime.getDate() + 7);
		}

		setOnboarding({
			...onboarding,
			showAtTime: showAgainTime,
		});

		// If we're transitioning from the intro modal to the contacts modal,
		// skip closing the contacts modal — it was triggered by the programmatic
		// isOpen→false on the first modal, not by the user dismissing the flow.
		if (openingContactsModal.current) {
			openingContactsModal.current = false;
			return;
		}

		setShowContactsModal(false);
	};

	const saveAndContinue = async () => {
		if (continueButton?.save) {
			setIsLoading(true);
			try {
				const success = await continueButton.save();
				if (success) {
					setStepperCount(stepperCount + 1);
				}
			} catch (error) {
				console.log(error);
			} finally {
				setIsLoading(false);
			}
		}
	};

	useEffect(() => {
		if (stepContent.length === 0 && systems && user) {
			const content: JSX.Element[] = [
				<PostLoginOnboardningName key={"Name"} setContinueButton={setContinueButton} />,
				<PostLoginOnboardningContactInfo key={"contactInfo"} setContinueButton={setContinueButton} />,
				<PostLoginOnboardningAdress key={"adress"} setContinueButton={setContinueButton} />,
			];

			if ((user.contacts.length ?? 0) === 0) {
				content.push(<PostLoginOnboardningContacts key={"contacts"} setContinueButton={setContinueButton} />);
			}

			if (systems.length === 1) {
				content.push(
					<PostLoginOnboardningBankAccount key={"backAccount"} setContinueButton={setContinueButton} />,
				);
			}

			content.push(<PostLoginOnboardningDone key={"done"} />);
			setStepContent(content);
		}
	}, [stepContent, systems, user]);

	const [onboadingSystems, setOnboadingSystems] = useState<OnboadingSystem[]>();
	useEffect(() => {
		if (systems && onboarding) {
			if (onboarding.onboardedSystems) {
				setOnboadingSystems(
					systems.map((s) => {
						return {
							system: s.name,
							name: s.commonName,
							isOnboarded: onboarding.onboardedSystems.some((onboadredSystem) => onboadredSystem === s.name),
						};
					}),
				);
			}
		}
	}, [systems, onboarding]);

	const [showModal, setShowModal] = useState(false);

	useEffect(() => {
		if (
			!showContactsModal &&
			!onboarding.showBiometric &&
			!onboarding.showNotification &&
			onboadingSystems &&
			!onboadingSystems.some((s) => !s.isOnboarded) &&
			onboarding.showAtTime !== undefined &&
			isTodayOrBefore(onboarding.showAtTime)
		) {
			const timer = setTimeout(() => {
				setShowModal(true);
			}, 1500);
			return () => clearTimeout(timer);
		} else {
			setShowModal(false);
		}
	}, [onboarding, setShowModal, showContactsModal, onboadingSystems]);

	return (
		<>
			{onboarding?.showAtTime && isTodayOrBefore(onboarding.showAtTime) && (
				<>
					<link rel="preload" href={myProfile} as="image" type="image/avif" />
					<link rel="preload" href={progressSpinner} as="image" type="image/avif" />
				</>
			)}

			<SystemOnboadringModal
				isOpen={
					onboadingSystems !== undefined &&
					onboadingSystems.length > 0 &&
					onboadingSystems.some((os) => !os.isOnboarded)
				}
				onClose={() => setOnboarding({ ...onboarding, onboardedSystems: systems.map((s) => s.name) })}
				onboadingSystems={onboadingSystems ?? []}
				firstTime={(onboarding.onboardedSystems?.length ?? 0) === 0}
			/>

			<PushNotificationModal
				isOpen={onboarding.showNotification}
				onClose={() => setOnboarding({ ...onboarding, showNotification: false })}
			/>
			<PasswordlessModal
				isOpen={onboarding.showBiometric}
				onClose={() => setOnboarding({ ...onboarding, showBiometric: false })}
			/>

			<Modal isOpen={showModal} onClose={onClose} closeOnOverlayClick={false}>
				<ModalHeader data-testid="setprofile-modal-header">
					<Flex alignItems={"center"} m={ThemeSpaceVariable.Medium} justifyContent={"space-between"}>
						<Heading as={"h1"}>{t("post_login_onboarding_set_profile")}</Heading>
						<ModalCloseButton></ModalCloseButton>
					</Flex>
				</ModalHeader>
				<ModalBody data-testid="setprofile-modal-body">
					<Stack gap={ThemeSpaceVariable.Large} p={ThemeSpaceVariable.Medium}>
						<Image src={myProfile} />
						<Text>{t("post_login_onboarding_set_profile_description")}</Text>
					</Stack>
					<ModalFooter paddingBottom={safeArea.bottomAsPx()}>
						<Box w={"100%"} p={ThemeSpaceVariable.Medium} pb={ThemeSpaceVariable.Large}>
							<Button
								variant={"primary"}
								w={"100%"}
								size={"lg"}
								onClick={() => {
									openingContactsModal.current = true;
									setShowContactsModal(true);
								}}
							>
								{t("post_login_onboarding_set_profile_button")}
							</Button>
						</Box>
					</ModalFooter>
				</ModalBody>
			</Modal>
			<Modal isOpen={showContactsModal} onClose={onClose} closeOnOverlayClick={false}>
				<ModalHeader p={ThemeSpaceVariable.Medium}>
					<Box ml={"auto"} w={"fit-content"}>
						<ModalCloseButton></ModalCloseButton>
					</Box>
					<Flex mt={"-1rem"} alignItems={"center"}>
						{stepperCount > 1 && (
							<IconButton
								aria-label="back"
								icon={BackIcon}
								variant="ghost"
								pos={"absolute"}
								onClick={() => setStepperCount(stepperCount - 1)}
							/>
						)}
						<Heading as="h5" fontSize={ThemeFontSizeVariable.Medium} textAlign={"center"} margin={"auto"}>
							{t("post_login_onboarding_set_profile")}
						</Heading>
					</Flex>
					<Progress.Root
						min={0}
						max={stepContent.length}
						className="progressbar"
						size={"xs"}
						m={ThemeSpaceVariable.Medium}
						value={stepperCount}
					>
						<Progress.Track>
							<Progress.Range />
						</Progress.Track>
					</Progress.Root>
				</ModalHeader>
				{stepContent.length > 0 && (
					<ModalBody p={ThemeSpaceVariable.Medium} minH={"100%"}>
						{stepContent[stepperCount - 1]}
					</ModalBody>
				)}
				<ModalFooter paddingBottom={safeArea.bottomAsPx()}>
					<Flex
						justify={"space-around"}
						w={"100%"}
						p={ThemeSpaceVariable.Medium}
						pb={ThemeSpaceVariable.Large}
					>
						{stepperCount < stepContent.length ? (
							<>
								<Button variant={"ghost"} size={"lg"} onClick={() => setStepperCount(stepperCount + 1)}>
									{t("choice_not_now")}
								</Button>
								<Button
									variant={"primary"}
									size={"lg"}
									onClick={() => {
										saveAndContinue();
									}}
									isDisabled={continueButton?.isDisabled}
									isLoading={isLoading}
								>
									{t("choice_save_and_continue")}
								</Button>
							</>
						) : (
							<Button variant={"primary"} size={"lg"} w={"100%"} onClick={onClose}>
								{t("choice_done")}
							</Button>
						)}
					</Flex>
				</ModalFooter>
			</Modal>

			<PostLoginOnboardingColleaguesShifts
				showOnboarding={onboarding.showAtTime === undefined || !isToday(onboarding.showAtTime)}
			/>
			{children}
		</>
	);
};
